import React from "react";
import { CustomerIdContext } from "context/CustomerIdContext";
import ViewAddresses from "./ViewAddresses";
import ViewAssets from "./ViewAssets";
import ViewSubscription from "./ViewSubscriptions";
import ViewDivisions from "./ViewDivisions";
import ViewMembers from "./ViewMembers";
import ViewCustomerTickets from "./ViewCustomerTickets";
import ViewCustomPlans from "./ViewCustomPlans";
import TransactionsList from "layouts/Transactions/TransactionsList";

export type CustomerSectionType =
  | "addresses"
  | "assets"
  | "subscriptions"
  | "custom_plans"
  | "transactions"
  | "divisions"
  | "members"
  | "tickets";

interface CustomerSectionOverlayContentProps {
  customerId: number;
  section: CustomerSectionType;
  /** Pass when section is "addresses" so Add Address form knows company vs customer */
  customerType?: string;
  /** When provided and section is addresses, "Add Address" closes this overlay and parent opens Add Address overlay */
  onOpenAddAddress?: (customerId: number, customerType: string) => void;
  /** When provided and section is assets, "Add Asset" closes this overlay and parent opens Add Asset overlay */
  onOpenAddAsset?: (customerId: number, customerType: string) => void;
  /** When provided and section is subscriptions, "Add Subscription" closes this overlay and parent opens Add Subscription overlay */
  onOpenAddSubscription?: (customerId: number, customerType: string) => void;
  /** When provided and section is members, "Add Member" closes this overlay and parent opens Add Member overlay */
  onOpenAddMember?: (customerId: number) => void;
  /** When provided and section is tickets, clicking a ticket closes this overlay and parent opens ticket detail overlay */
  onOpenTicketDetail?: (ticketId: number) => void;
  /** When provided and section is tickets, "Add Ticket" closes this overlay and parent opens Add Ticket overlay */
  onOpenAddTicket?: (customerId: number) => void;
}

/**
 * Renders a single customer section (Addresses, Assets, etc.) inside a slide overlay
 * with CustomerIdContext so the view components work without route params.
 */
const CustomerSectionOverlayContent = ({
  customerId,
  section,
  customerType,
  onOpenAddAddress,
  onOpenAddAsset,
  onOpenAddSubscription,
  onOpenAddMember,
  onOpenTicketDetail,
  onOpenAddTicket,
}: CustomerSectionOverlayContentProps) => {
  return (
    <CustomerIdContext.Provider value={customerId}>
      {section === "addresses" && (
        <ViewAddresses
          customerType={customerType}
          onOpenAddAddress={onOpenAddAddress}
        />
      )}
      {section === "assets" && (
        <ViewAssets
          onOpenAddAsset={onOpenAddAsset}
          customerType={customerType}
        />
      )}
      {section === "subscriptions" && (
        <ViewSubscription
          onOpenAddSubscription={onOpenAddSubscription}
          customerType={customerType}
        />
      )}
      {section === "custom_plans" && <ViewCustomPlans />}
      {section === "transactions" && (
        <TransactionsList embedded fixedCustomerId={customerId} />
      )}
      {section === "divisions" && <ViewDivisions />}
      {section === "members" && (
        <ViewMembers onOpenAddMember={onOpenAddMember} />
      )}
      {section === "tickets" && (
        <ViewCustomerTickets
          onOpenTicketDetail={onOpenTicketDetail}
          onOpenAddTicket={onOpenAddTicket}
        />
      )}
    </CustomerIdContext.Provider>
  );
};

export default CustomerSectionOverlayContent;
